/**
 * NewDiagramDialog Component
 *
 * The New diagram dialog (task 5.3, Requirements 6.9, 8.6, 9.5). It collects a
 * Diagram_Title (required, max 200), an optional Diagram_Folder (chosen from the
 * folders already in use or typed as a new kebab-case one, validated), and a
 * starting Template, then creates the diagram and opens it in the editor.
 *
 * Create flow (per the design's "Create flow"): reserve an unused Diagram_Id →
 * build the template's scene → apply the title to the template → export the
 * Diagram_File SVG with that id and title → create the diagram. GitLab rejects a
 * `create` for a path that already exists, which covers the race between
 * reserving and creating; the backend maps that to 409 `ID_TAKEN`, so this
 * dialog retries once with a freshly reserved id. On success it navigates to
 * `/diagrams/{id}`. Any failure shows an inline error and does not navigate.
 *
 * The "Import files" starting point is task 5.5: this dialog exposes a `mode`
 * seam (`"template" | "import"`) and renders only the template mode for now, so
 * the import step can be slotted in without reworking the dialog shell.
 */

import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import SingleSelectCombobox, {
  type ComboboxOption,
} from "@/components/shared/SingleSelectCombobox"
import { useAuth } from "@/hooks/useAuth"
import {
  createDiagram,
  reserveDiagramId,
  DiagramsApiError,
  type DiagramSummary,
} from "@/services/diagramsService"
import { exportDiagramSvg, type DiagramScene } from "@/components/diagrams/diagramSvg"
import { renderMermaidSvg } from "@/components/diagrams/mermaidSvg"
import {
  applyTemplateTitle,
  DIAGRAM_TEMPLATES,
  type DiagramTemplate,
} from "@/components/diagrams/templates"
import {
  MERMAID_TEMPLATES,
  type MermaidTemplate,
} from "@/components/diagrams/mermaidTemplates"
import {
  MAX_TITLE_LENGTH,
  validateFolder,
  validateTitle,
} from "@/components/diagrams/newDiagramForm"
import ImportFilesStep from "@/components/diagrams/ImportFilesStep"
import type { DiagramKind } from "@/services/diagramsService"

/**
 * The dialog's starting-point mode (Requirement 9.5): `"template"` starts from
 * a bundled Template; `"import"` brings in existing files (Requirement 16, via
 * `ImportFilesStep`).
 */
type NewDiagramMode = "template" | "import"

/** Props for the NewDiagramDialog component. */
interface NewDiagramDialogProps {
  /** Whether the dialog is open. */
  open: boolean
  /** Called with the next open state (false when the dialog should close). */
  onOpenChange: (open: boolean) => void
  /**
   * The Diagram_Folders already in use, offered as existing choices in the
   * folder field. The user may also type a new one. Order is preserved.
   */
  existingFolders: string[]
  /**
   * Called after an import that created several diagrams, with the folder they
   * went into (null when unfiled). The browser uses it to filter to that folder
   * and refresh its list, since navigating to `/diagrams` while already there
   * would not reload it (Requirement 16.6).
   */
  onImportedSeveral: (folder: string | null) => void
}

/**
 * Create one diagram from a template and return its id to navigate to.
 *
 * Runs the full create flow once: reserve an id, build and title the template
 * scene, export the Diagram_File, and create it. On a 409 `ID_TAKEN` (the
 * reserve/create race), it retries exactly once with a new id; any other error,
 * or a second `ID_TAKEN`, propagates to the caller.
 *
 * @param params - The create inputs.
 * @param params.token - The Auth0 access token for the API calls.
 * @param params.title - The trimmed Diagram_Title.
 * @param params.folder - The Diagram_Folder, or null when unfiled.
 * @param params.template - The chosen Template whose scene to build.
 * @returns The created diagram's metadata.
 * @throws DiagramsApiError or Error if creation fails (after the one retry).
 */
async function createFromTemplate({
  token,
  title,
  folder,
  template,
}: {
  token: string
  title: string
  folder: string | null
  template: DiagramTemplate
}): Promise<DiagramSummary> {
  // One attempt: reserve an id, build the scene with the title applied, export
  // the SVG with that id, and create. Returns the created diagram.
  const attempt = async (): Promise<DiagramSummary> => {
    const id: string = await reserveDiagramId(token)
    const titled: DiagramScene = applyTemplateTitle(template.buildScene(), title)
    // The new title is longer or shorter than the placeholder, so let
    // Excalidraw re-measure text elements; otherwise a long title keeps the
    // placeholder's width and can spill past the exported image's edge.
    const { restoreElements } = await import("@excalidraw/excalidraw")
    const scene: DiagramScene = {
      ...titled,
      // `refreshDimensions` only runs when `repairBindings` is also set (0.18.1).
      elements: restoreElements(titled.elements, null, {
        refreshDimensions: true,
        repairBindings: true,
      }),
    }
    const svg: string = await exportDiagramSvg({ scene, id, title })
    return createDiagram(token, { id, kind: "excalidraw", title, folder, svg })
  }

  try {
    return await attempt()
  } catch (caught: unknown) {
    // The reserved id was taken between reserve and create: retry once with a
    // fresh id. Any other failure (or a second clash) is for the caller.
    if (caught instanceof DiagramsApiError && caught.code === "ID_TAKEN") {
      return attempt()
    }
    throw caught
  }
}

/**
 * Create one Mermaid_Plot from a starting source and return its id to navigate
 * to.
 *
 * Mirrors {@link createFromTemplate} for the Mermaid kind: reserve an id, render
 * the Rendered_Svg from the source with that id and title, and create the plot
 * with `kind: "mermaid"`, the source, and the svg. Retries once on a 409
 * `ID_TAKEN` (the reserve/create race); any other error, or a second
 * `ID_TAKEN`, propagates to the caller.
 *
 * @param params - The create inputs.
 * @param params.token - The Auth0 access token for the API calls.
 * @param params.title - The trimmed Diagram_Title.
 * @param params.folder - The Diagram_Folder, or null when unfiled.
 * @param params.source - The starting Mermaid_Source to render and commit.
 * @returns The created diagram's metadata.
 * @throws DiagramsApiError or Error if creation fails (after the one retry), or
 *   if the source does not render.
 */
async function createMermaidPlot({
  token,
  title,
  folder,
  source,
}: {
  token: string
  title: string
  folder: string | null
  source: string
}): Promise<DiagramSummary> {
  // One attempt: reserve an id, render the Rendered_Svg with that id, and
  // create the plot. Returns the created diagram.
  const attempt = async (): Promise<DiagramSummary> => {
    const id: string = await reserveDiagramId(token)
    const svg: string = await renderMermaidSvg({ source, id, title })
    return createDiagram(token, { id, kind: "mermaid", title, folder, svg, source })
  }

  try {
    return await attempt()
  } catch (caught: unknown) {
    if (caught instanceof DiagramsApiError && caught.code === "ID_TAKEN") {
      return attempt()
    }
    throw caught
  }
}

/**
 * The New diagram dialog.
 *
 * @param props - Component props.
 * @param props.open - Whether the dialog is open.
 * @param props.onOpenChange - Open-state change handler.
 * @param props.existingFolders - Folders already in use, offered as choices.
 * @returns The rendered dialog.
 */
export default function NewDiagramDialog({
  open,
  onOpenChange,
  existingFolders,
  onImportedSeveral,
}: NewDiagramDialogProps): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const navigate = useNavigate()

  // The Diagram_Kind to create. Excalidraw is the default so the existing flow
  // is unchanged for current users (Requirement 4.1).
  const [kind, setKind] = useState<DiagramKind>("excalidraw")

  // The starting-point mode (excalidraw only): a Template, or importing files.
  const [mode, setMode] = useState<NewDiagramMode>("template")

  // Form fields.
  const [title, setTitle] = useState<string>("")
  const [folder, setFolder] = useState<string>("")
  const [templateId, setTemplateId] = useState<string>(DIAGRAM_TEMPLATES[0].id)
  // The chosen Mermaid starter (used only when `kind` is "mermaid").
  const [mermaidTemplateId, setMermaidTemplateId] = useState<string>(
    MERMAID_TEMPLATES[0].id,
  )

  // Submission state. `submitError` is the inline error shown on failure (the
  // dialog stays open and does not navigate).
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // The existing folders as combobox options, in the order given.
  const folderOptions = useMemo<ComboboxOption[]>(
    () => existingFolders.map((name: string): ComboboxOption => ({ value: name, label: name })),
    [existingFolders],
  )

  // Live validation messages for the two free-text-ish fields. The title error
  // is only surfaced after a submit attempt (see `showTitleError`) so the field
  // does not shout before the user has typed.
  const titleError: string | null = validateTitle(title)
  const folderError: string | null = validateFolder(folder)

  /**
   * Reset the form to its initial state. Called when the dialog closes so a
   * reopen starts clean.
   */
  const resetForm = useCallback((): void => {
    setKind("excalidraw")
    setMode("template")
    setTitle("")
    setFolder("")
    setTemplateId(DIAGRAM_TEMPLATES[0].id)
    setMermaidTemplateId(MERMAID_TEMPLATES[0].id)
    setSubmitError(null)
    setSubmitting(false)
  }, [])

  /**
   * Handle the outcome of an import run (Requirement 16.6). Close the dialog,
   * then: for a single created diagram, open it in the editor; for several,
   * stay on the browser filtered to the folder they went into. The summary
   * toast for several files is shown by `ImportFilesStep` itself.
   *
   * @param outcome - The import outcome from `ImportFilesStep`.
   * @param outcome.createdIds - The ids created this run, in creation order.
   * @param outcome.folder - The folder the diagrams went into.
   */
  const handleImported = useCallback(
    ({ createdIds, folder: importedFolder }: { createdIds: string[]; folder: string | null }): void => {
      resetForm()
      onOpenChange(false)
      if (createdIds.length === 1) {
        navigate(`/diagrams/${createdIds[0]}`)
      } else {
        // Several: stay on the browser, filtered to the chosen folder.
        onImportedSeveral(importedFolder)
      }
    },
    [resetForm, onOpenChange, navigate, onImportedSeveral],
  )

  /**
   * Handle the dialog's open-state changes, resetting the form on close so the
   * next open is fresh. A close while submitting is ignored.
   *
   * @param nextOpen - The requested open state.
   */
  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (!nextOpen && submitting) {
        return
      }
      if (!nextOpen) {
        resetForm()
      }
      onOpenChange(nextOpen)
    },
    [submitting, resetForm, onOpenChange],
  )

  /**
   * Validate the inputs, create the diagram, and navigate to the editor. Shows
   * an inline error and stays open on any failure.
   */
  const handleCreate = useCallback(async (): Promise<void> => {
    setSubmitError(null)

    // Re-validate at the trust boundary before any API call. These should
    // already be satisfied (the button is disabled otherwise) but are checked
    // here so the dialog never sends known-bad input.
    const trimmedTitle: string = title.trim()
    if (validateTitle(trimmedTitle) !== null || validateFolder(folder) !== null) {
      setSubmitError("Please fix the highlighted fields before creating.")
      return
    }

    setSubmitting(true)
    try {
      const token: string = await getAccessToken()
      const folderOrNull: string | null = folder === "" ? null : folder
      let created: DiagramSummary
      if (kind === "mermaid") {
        // The chosen Mermaid starter's source.
        const mermaidTemplate: MermaidTemplate | undefined = MERMAID_TEMPLATES.find(
          (candidate: MermaidTemplate): boolean => candidate.id === mermaidTemplateId,
        )
        if (mermaidTemplate === undefined) {
          setSubmitError("Please choose a starting point.")
          setSubmitting(false)
          return
        }
        created = await createMermaidPlot({
          token,
          title: trimmedTitle,
          folder: folderOrNull,
          source: mermaidTemplate.source,
        })
      } else {
        const template: DiagramTemplate | undefined = DIAGRAM_TEMPLATES.find(
          (candidate: DiagramTemplate): boolean => candidate.id === templateId,
        )
        if (template === undefined) {
          setSubmitError("Please choose a template.")
          setSubmitting(false)
          return
        }
        created = await createFromTemplate({
          token,
          title: trimmedTitle,
          folder: folderOrNull,
          template,
        })
      }
      // Success: close and open the new diagram in the editor (task 5.1 route).
      resetForm()
      onOpenChange(false)
      navigate(`/diagrams/${created.id}`)
    } catch (caught: unknown) {
      // Fail loudly: keep the user's input, show the error, do not navigate.
      setSubmitError(
        caught instanceof Error ? caught.message : "Failed to create the diagram.",
      )
      setSubmitting(false)
    }
  }, [
    kind,
    title,
    folder,
    templateId,
    mermaidTemplateId,
    getAccessToken,
    resetForm,
    onOpenChange,
    navigate,
  ])

  // Import is an Excalidraw-only starting point (importing `.mmd` files is out
  // of scope for v1), so it applies only when the mode is "import" and the kind
  // is excalidraw. Everything else shows the title + starting-point form.
  const importing: boolean = kind === "excalidraw" && mode === "import"

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New diagram</DialogTitle>
          <DialogDescription>
            {importing
              ? "Choose a folder, then add the Excalidraw files to import into it."
              : "Pick a kind, give the diagram a title, choose a folder, and pick a starting point."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Kind picker (Requirement 4.1): Excalidraw drawing or Mermaid plot,
              Excalidraw default so the existing flow is unchanged. */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Kind</span>
            <div className="flex gap-2" role="radiogroup" aria-label="Diagram kind">
              <button
                type="button"
                role="radio"
                aria-checked={kind === "excalidraw"}
                onClick={() => setKind("excalidraw")}
                disabled={submitting}
                className={
                  kind === "excalidraw"
                    ? "flex-1 rounded-md border border-primary bg-accent px-3 py-2 text-sm"
                    : "flex-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                }
              >
                Excalidraw drawing
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={kind === "mermaid"}
                onClick={() => setKind("mermaid")}
                disabled={submitting}
                className={
                  kind === "mermaid"
                    ? "flex-1 rounded-md border border-primary bg-accent px-3 py-2 text-sm"
                    : "flex-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                }
              >
                Mermaid plot
              </button>
            </div>
          </div>

          {/* Starting-point switch (Requirement 9.5), Excalidraw only: a
              Template, or Import files (Requirement 16). Mermaid has no import
              in v1, so this is hidden for it. The folder field is shared. */}
          {kind === "excalidraw" && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Start from</span>
              <div className="flex gap-2" role="radiogroup" aria-label="Starting point">
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "template"}
                  onClick={() => setMode("template")}
                  disabled={submitting}
                  className={
                    mode === "template"
                      ? "flex-1 rounded-md border border-primary bg-accent px-3 py-2 text-sm"
                      : "flex-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                  }
                >
                  Template
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "import"}
                  onClick={() => setMode("import")}
                  disabled={submitting}
                  className={
                    mode === "import"
                      ? "flex-1 rounded-md border border-primary bg-accent px-3 py-2 text-sm"
                      : "flex-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                  }
                >
                  Import files
                </button>
              </div>
            </div>
          )}

          {/* Title — shown unless importing. In import mode each file carries
              its own title (Requirement 16.3), so there is no single shared one. */}
          {!importing && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-diagram-title" className="text-sm font-medium">
                Title
              </label>
              <Input
                id="new-diagram-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Oxpecker architecture"
                maxLength={MAX_TITLE_LENGTH}
                aria-invalid={titleError !== null && title.trim() !== ""}
                aria-describedby={titleError !== null ? "new-diagram-title-error" : undefined}
                disabled={submitting}
              />
              {/* Only nag about the title once the user has typed something. */}
              {titleError !== null && title.trim() !== "" && (
                <p id="new-diagram-title-error" className="text-xs text-destructive">
                  {titleError}
                </p>
              )}
            </div>
          )}

          {/* Folder — shared by both modes. */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="new-diagram-folder" className="text-sm font-medium">
              Folder <span className="text-muted-foreground">(optional)</span>
            </label>
            <SingleSelectCombobox
              id="new-diagram-folder"
              options={folderOptions}
              value={folder}
              onChange={setFolder}
              allowCustomValue
              placeholder="Unfiled — or choose/type a folder"
              emptyMessage="Type a new folder name"
              disabled={submitting}
            />
            {folderError !== null && (
              <p className="text-xs text-destructive">{folderError}</p>
            )}
          </div>

          {/* Excalidraw template picker — excalidraw template mode only. */}
          {kind === "excalidraw" && mode === "template" && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Template</span>
              <div
                className="flex flex-col gap-2"
                role="radiogroup"
                aria-label="Starting template"
              >
                {DIAGRAM_TEMPLATES.map((template: DiagramTemplate) => {
                  const selected: boolean = template.id === templateId
                  return (
                    <button
                      key={template.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setTemplateId(template.id)}
                      disabled={submitting}
                      className={
                        selected
                          ? "flex items-center justify-between rounded-md border border-primary bg-accent px-3 py-2 text-left text-sm"
                          : "flex items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-muted"
                      }
                    >
                      <span>{template.name}</span>
                      {selected && (
                        <span
                          aria-hidden="true"
                          className="size-2 rounded-full bg-primary"
                        />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Mermaid starter picker — mermaid only. */}
          {kind === "mermaid" && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Starting point</span>
              <div
                className="flex flex-col gap-2"
                role="radiogroup"
                aria-label="Starting Mermaid template"
              >
                {MERMAID_TEMPLATES.map((template: MermaidTemplate) => {
                  const selected: boolean = template.id === mermaidTemplateId
                  return (
                    <button
                      key={template.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setMermaidTemplateId(template.id)}
                      disabled={submitting}
                      className={
                        selected
                          ? "flex items-center justify-between rounded-md border border-primary bg-accent px-3 py-2 text-left text-sm"
                          : "flex items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-muted"
                      }
                    >
                      <span>{template.name}</span>
                      {selected && (
                        <span
                          aria-hidden="true"
                          className="size-2 rounded-full bg-primary"
                        />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {!importing && submitError !== null && (
            <p role="alert" className="text-sm text-destructive">
              {submitError}
            </p>
          )}

          {/* Import step — excalidraw import mode only. It owns its own Import
              button and the folder-blocked guard, so the dialog footer's Create
              button is hidden then. The folder error blocks importing too. */}
          {importing && (
            <ImportFilesStep
              folder={folder === "" ? null : folder}
              disabled={submitting || folderError !== null}
              onImported={handleImported}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            {importing ? "Close" : "Cancel"}
          </Button>
          {/* Create confirms here for both kinds; import mode confirms inside
              the import step instead. */}
          {!importing && (
            <Button
              onClick={() => void handleCreate()}
              // Block submit until the title is valid and the folder (if any) is
              // valid; the handler re-checks at the trust boundary regardless.
              disabled={submitting || titleError !== null || folderError !== null}
            >
              {submitting && <Loader2 className="size-4 animate-spin" data-icon="inline-start" />}
              {submitting ? "Creating…" : "Create"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
